import {
  isUserOnboardingInProgress,
  UserProfile,
} from "src/app/pages/profile/profile.model";
import { StoreGetDTO } from "src/app/pages/store-management/stores/store";

export const INITIAL_SETUP_WELCOME_URL = "/initial-setup/welcome";

export const INITIAL_SETUP_CANDIDATE_CLAIMS_URL =
  "/initial-setup/candidate-claims-found";
/**
 * Same rules as {@link redirectToFirstTimeUserExperienceGuard} welcome redirect.
 * Not gated on linked-store count: a user can already have a (support-reset,
 * ops-edited, etc.) store and still never have seen the welcome screen, and
 * should still see it before anything else.
 */
export function shouldRedirectToInitialSetupWelcome(
  profile: UserProfile | null | undefined,
): boolean {
  if (!isUserOnboardingInProgress(profile)) {
    return false;
  }
  return !profile?.onboardingConfig?.hasSeenWelcomeScreen;
}

/** Completed stores the user owns; stores shared with them are excluded. */
export function countCompletedOwnedStores(stores: StoreGetDTO[]): number {
  return stores.filter(
    (store) =>
      store.onboarding?.isComplete === true && !store.isBillingDelegated,
  ).length;
}

/**
 * The store the candidate-claims step follows: the first owned, configured one.
 * Shared stores were analyzed under their owner, so they never qualify, even
 * when they come first in the list.
 */
export function getOnboardingStore(
  stores: StoreGetDTO[] | null | undefined,
): StoreGetDTO | undefined {
  return stores?.find(
    (store) => !store.isBillingDelegated && store.onboarding?.isComplete === true,
  );
}

/**
 * Where a user is in first-time onboarding. Single source of truth: guards,
 * the dashboard and the stores view should read this instead of re-deriving
 * it from store counts.
 *
 * - `complete`: onboarding finished.
 * - `welcome`: welcome screen not seen yet.
 * - `link-first-store`: no stores at all.
 * - `finish-store-setup`: stores exist but none is ready to move on yet.
 * - `candidate-claims`: at least one owned store is configured; next come the
 *   candidate claims and billing.
 * - `delegated-ready`: every store is shared with the user and configured;
 *   nothing to link or pay, so onboarding only needs to be closed.
 *
 * Mixed accounts (owned + shared) follow their owned stores: those are the ones
 * with claims and billing. Shared stores never gate the flow there.
 */
export type OnboardingStage =
  | "complete"
  | "welcome"
  | "link-first-store"
  | "finish-store-setup"
  | "candidate-claims"
  | "delegated-ready";

export function getOnboardingStage(
  profile: UserProfile | null | undefined,
  stores: StoreGetDTO[],
): OnboardingStage {
  if (!isUserOnboardingInProgress(profile)) {
    return "complete";
  }
  if (shouldRedirectToInitialSetupWelcome(profile)) {
    return "welcome";
  }
  if (stores.length === 0) {
    return "link-first-store";
  }

  const hasOwnedStores = stores.some((store) => !store.isBillingDelegated);
  if (hasOwnedStores) {
    return countCompletedOwnedStores(stores) > 0
      ? "candidate-claims"
      : "finish-store-setup";
  }

  return stores.every((store) => store.onboarding?.isComplete === true)
    ? "delegated-ready"
    : "finish-store-setup";
}

/**
 * Initial-setup URL when onboarding is incomplete, or null for main app entry.
 * Matches {@link redirectToFirstTimeUserExperienceGuard}.
 * Candidate claims only apply to owned stores; shared stores were analyzed when
 * their owner linked them and the owner pays for them, so they skip the flow.
 */
export function resolveInitialSetupOnboardingUrl(
  profile: UserProfile | null | undefined,
  stores: StoreGetDTO[],
): string | null {
  switch (getOnboardingStage(profile, stores)) {
    case "welcome":
      return INITIAL_SETUP_WELCOME_URL;
    case "candidate-claims":
      return INITIAL_SETUP_CANDIDATE_CLAIMS_URL;
    default:
      return null;
  }
}
